'use client';
import React, { useRef, useState } from 'react';
import { Upload, FileText, Image, X, CheckCircle, AlertTriangle } from 'lucide-react';
import type { OrderState } from './OrderWizardContent';

interface Props {
  order: OrderState;
  updateOrder: (updates: Partial<OrderState>) => void;
  showErrors?: boolean;
}

type DesignBriefFields = {
  businessName: string;
  tagline: string;
  phone: string;
  website: string;
  offer: string;
  colors: string;
  notes: string;
};

const EMPTY_BRIEF: DesignBriefFields = {
  businessName: '',
  tagline: '',
  phone: '',
  website: '',
  offer: '',
  colors: '',
  notes: '',
};

function serializeBrief(fields: DesignBriefFields): string {
  return [
    fields.businessName && `Business: ${fields.businessName}`,
    fields.tagline && `Tagline: ${fields.tagline}`,
    fields.phone && `Phone: ${fields.phone}`,
    fields.website && `Website: ${fields.website}`,
    fields.offer && `Offer: ${fields.offer}`,
    fields.colors && `Colors: ${fields.colors}`,
    fields.notes && `Notes: ${fields.notes}`,
  ]
    .filter(Boolean)
    .join(' | ');
}

export default function StepE({ order, updateOrder, showErrors }: Props) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [dragOver, setDragOver] = useState(false);
  const [briefFields, setBriefFields] = useState<DesignBriefFields>(EMPTY_BRIEF);
  const [briefErrors, setBriefErrors] = useState<Partial<Record<keyof DesignBriefFields, boolean>>>({});

  const missingArtworkType = showErrors && !order.artworkType;
  const missingFile = showErrors && order.artworkType === 'upload' && !order.artworkFile;
  const missingBusinessName = showErrors && order.artworkType === 'design-service' && !briefFields.businessName;
  const missingOffer = showErrors && order.artworkType === 'design-service' && !briefFields.offer;

  const handleFile = (file: File) => {
    const validTypes = ['image/jpeg', 'image/png', 'application/pdf'];
    if (!validTypes.includes(file.type)) {
      return;
    }
    updateOrder({ artworkFile: file, artworkType: 'upload' });
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file) handleFile(file);
  };

  const updateBriefField = (key: keyof DesignBriefFields, value: string) => {
    const updated = { ...briefFields, [key]: value };
    setBriefFields(updated);
    setBriefErrors(prev => ({ ...prev, [key]: false }));
    updateOrder({ designBrief: serializeBrief(updated) });
  };

  return (
    <div className="animate-slide-up">
      <div className="mb-6">
        <h2 className="font-display text-2xl font-700 text-white mb-1">Artwork</h2>
        <p className="text-sm text-slate-400">
          {order.addDesign
            ? 'You selected our design service. Fill in the brief below and our team will create your ad.' :'Upload your print-ready artwork file (8.5×5.5, 300dpi minimum).'}
        </p>
      </div>

      {/* Choose mode */}
      <div className="flex gap-3 mb-6">
        <button
          type="button"
          onClick={() => updateOrder({ artworkType: 'upload' })}
          className={`flex-1 py-3 text-sm font-semibold rounded-xl border transition-all ${
            order.artworkType === 'upload' ?'border-brand-500/60 bg-brand-600/12 text-brand-300' :'border-white/10 text-slate-400 hover:border-brand-500/30 hover:text-white'
          }`}
        >
          Upload Artwork
        </button>
        <button
          type="button"
          onClick={() => updateOrder({ artworkType: 'design-service' })}
          className={`flex-1 py-3 text-sm font-semibold rounded-xl border transition-all ${
            order.artworkType === 'design-service' ?'border-brand-500/60 bg-brand-600/12 text-brand-300' :'border-white/10 text-slate-400 hover:border-brand-500/30 hover:text-white'
          }`}
        >
          Design Service Intake
        </button>
      </div>

      {order.artworkType === 'upload' && (
        <div>
          {/* Drop zone */}
          <div
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
            className={`relative border-2 border-dashed rounded-2xl p-10 text-center cursor-pointer transition-all ${
              dragOver
                ? 'border-brand-500 bg-brand-600/10'
                : order.artworkFile
                ? 'border-green-500/50 bg-green-500/8'
                : missingFile
                ? 'border-red-500/50 hover:border-red-400/70' :'border-white/15 hover:border-brand-500/50 hover:bg-white/3'
            }`}
          >
            <input
              ref={fileInputRef}
              type="file"
              accept=".jpg,.jpeg,.png,.pdf"
              className="hidden"
              onChange={(e) => e.target.files?.[0] && handleFile(e.target.files[0])}
            />

            {order.artworkFile ? (
              <div className="flex flex-col items-center">
                <div className="w-12 h-12 rounded-xl bg-green-500/15 border border-green-500/30 flex items-center justify-center mb-3">
                  <CheckCircle size={22} className="text-green-400" />
                </div>
                <p className="font-semibold text-white mb-1">{order.artworkFile.name}</p>
                <p className="text-xs text-slate-500 mb-3">
                  {(order.artworkFile.size / 1024 / 1024).toFixed(2)} MB · {order.artworkFile.type}
                </p>
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); updateOrder({ artworkFile: null }); }}
                  className="flex items-center gap-1.5 text-xs text-red-400 hover:text-red-300 transition-colors"
                >
                  <X size={12} />
                  Remove file
                </button>
              </div>
            ) : (
              <div className="flex flex-col items-center">
                <div className="w-12 h-12 rounded-xl bg-brand-600/15 border border-brand-600/25 flex items-center justify-center mb-4">
                  <Upload size={22} className="text-brand-400" />
                </div>
                <p className="font-semibold text-white mb-1">Drop your file here or click to browse</p>
                <p className="text-xs text-slate-500">JPG, PNG, or PDF · Max 50MB · 8.5×5.5 at 300dpi</p>
              </div>
            )}
          </div>

          {/* Upload required notice */}
          {!order.artworkFile && (
            <div className={`mt-3 flex items-center gap-2 p-3 rounded-xl border ${missingFile ? 'bg-red-500/8 border-red-500/25' : 'bg-amber-500/8 border-amber-500/20'}`}>
              <AlertTriangle size={13} className={`shrink-0 ${missingFile ? 'text-red-400' : 'text-amber-400'}`} />
              <p className="text-xs text-slate-400">
                {missingFile ? 'Artwork file is required to continue.' : 'Upload your artwork file to continue.'}
              </p>
            </div>
          )}

          {/* Spec reminder */}
          <div className="mt-4 p-4 rounded-xl bg-amber-500/8 border border-amber-500/20 flex items-start gap-2.5">
            <AlertTriangle size={14} className="text-amber-400 shrink-0 mt-0.5" />
            <div className="space-y-1">
              <p className="text-xs text-amber-400 font-semibold">Artwork Requirements</p>
              <ul className="text-xs text-slate-400 space-y-0.5">
                <li>• Dimensions: 8.5×5.5 inches (landscape)</li>
                <li>• Resolution: 300dpi minimum for print quality</li>
                <li>• Format: PDF (preferred), JPG, or PNG</li>
                <li>• Color mode: CMYK preferred for accurate print color</li>
              </ul>
            </div>
          </div>
        </div>
      )}

      {order.artworkType === 'design-service' && (
        <div className="space-y-4">
          <div className="p-4 rounded-xl bg-brand-600/10 border border-brand-600/20 flex items-center gap-3 mb-4">
            <div className="w-8 h-8 rounded-lg bg-gradient-primary flex items-center justify-center">
              <FileText size={14} className="text-white" />
            </div>
            <p className="text-sm text-slate-300">
              Our design team will contact you within 1 business day. Fill out the brief below so we can get started.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-1.5">
                Business Name <span className="text-red-400">*</span>
              </label>
              <input
                value={briefFields.businessName}
                onChange={(e) => updateBriefField('businessName', e.target.value)}
                className={`w-full px-4 py-3 bg-navy-700 border rounded-xl text-sm text-white placeholder-slate-500 focus:outline-none focus:border-brand-500 transition-colors ${missingBusinessName ? 'border-red-500/60' : briefErrors.businessName ? 'border-red-500/60' : 'border-white/12'}`}
                placeholder="Your business name"
              />
              {missingBusinessName && (
                <p className="mt-1 flex items-center gap-1 text-xs text-red-400">
                  <AlertTriangle size={11} />
                  Business name is required.
                </p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-1.5">Tagline / Slogan</label>
              <input
                value={briefFields.tagline}
                onChange={(e) => updateBriefField('tagline', e.target.value)}
                className="w-full px-4 py-3 bg-navy-700 border border-white/12 rounded-xl text-sm text-white placeholder-slate-500 focus:outline-none focus:border-brand-500 transition-colors"
                placeholder="Optional tagline"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-1.5">Phone Number</label>
              <input
                value={briefFields.phone}
                onChange={(e) => updateBriefField('phone', e.target.value)}
                className="w-full px-4 py-3 bg-navy-700 border border-white/12 rounded-xl text-sm text-white placeholder-slate-500 focus:outline-none focus:border-brand-500 transition-colors"
                placeholder="(813) 555-0192"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-1.5">Website</label>
              <input
                value={briefFields.website}
                onChange={(e) => updateBriefField('website', e.target.value)}
                className="w-full px-4 py-3 bg-navy-700 border border-white/12 rounded-xl text-sm text-white placeholder-slate-500 focus:outline-none focus:border-brand-500 transition-colors"
                placeholder="www.yourbusiness.com"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-300 mb-1.5">
              Special Offer / Coupon Text <span className="text-red-400">*</span>
            </label>
            <input
              value={briefFields.offer}
              onChange={(e) => updateBriefField('offer', e.target.value)}
              className={`w-full px-4 py-3 bg-navy-700 border rounded-xl text-sm text-white placeholder-slate-500 focus:outline-none focus:border-brand-500 transition-colors ${missingOffer ? 'border-red-500/60' : briefErrors.offer ? 'border-red-500/60' : 'border-white/12'}`}
              placeholder="e.g. 20% Off Your First Visit · Expires 06/30/26"
            />
            {missingOffer && (
              <p className="mt-1 flex items-center gap-1 text-xs text-red-400">
                <AlertTriangle size={11} />
                Special offer text is required.
              </p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-300 mb-1.5">Brand Colors (optional)</label>
            <input
              value={briefFields.colors}
              onChange={(e) => updateBriefField('colors', e.target.value)}
              className="w-full px-4 py-3 bg-navy-700 border border-white/12 rounded-xl text-sm text-white placeholder-slate-500 focus:outline-none focus:border-brand-500 transition-colors"
              placeholder="e.g. Red #E53E3E, White, Gold"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-300 mb-1.5">Additional Notes</label>
            <textarea
              value={briefFields.notes}
              onChange={(e) => updateBriefField('notes', e.target.value)}
              rows={3}
              className="w-full px-4 py-3 bg-navy-700 border border-white/12 rounded-xl text-sm text-white placeholder-slate-500 focus:outline-none focus:border-brand-500 transition-colors resize-none"
              placeholder="Logo references, style preferences, must-include information..."
            />
          </div>

          {/* Required fields notice */}
          {(!briefFields.businessName || !briefFields.offer) && !missingBusinessName && !missingOffer && (
            <div className="flex items-center gap-2 p-3 rounded-xl bg-amber-500/8 border border-amber-500/20">
              <AlertTriangle size={13} className="text-amber-400 shrink-0" />
              <p className="text-xs text-slate-400">Business Name and Special Offer are required to continue.</p>
            </div>
          )}

          <p className="text-xs text-slate-500">
            Upload any logos or reference images after checkout. You'll receive a link via email.
          </p>
        </div>
      )}

      {!order.artworkType && (
        <div className="text-center py-8">
          <Image size={32} className="text-slate-600 mx-auto mb-3" />
          <p className={`text-sm ${missingArtworkType ? 'text-red-400' : 'text-slate-500'}`}>
            {missingArtworkType ? 'Please select an artwork option to continue.' : 'Select an artwork option above to continue.'}
          </p>
          {missingArtworkType && (
            <p className="mt-1 flex items-center justify-center gap-1 text-xs text-red-400">
              <AlertTriangle size={11} />
              This field is required.
            </p>
          )}
        </div>
      )}
    </div>
  );
}